/**
 * A custom element that applies a discount to the cart.
 */

/**
 * Creates a fetch configuration object
 * @param {string} [type] The type of response to expect
 * @param {Object} [config] The config of the request
 * @param {FetchConfig['body']} [config.body] The body of the request
 * @param {FetchConfig['headers']} [config.headers] The headers of the request
 * @returns {RequestInit} The fetch configuration object
 */
function fetchConfig(type = 'json', config = {}) {
  /** @type {Headers} */
  const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers: /** @type {HeadersInit} */ (headers),
    body: config.body,
  };
}

class CartDiscount extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.cartDiscountError = this.querySelector("[ref='cartDiscountError']")
    this.cartDiscountErrorDiscountCode = this.querySelector("[ref='cartDiscountErrorDiscountCode']")
    this.cartDiscountErrorShipping = this.querySelector("[ref='cartDiscountErrorShipping']")
    this.querySelector("form").addEventListener('submit', this.applyDiscount.bind(this))

    if (this.querySelector(".cart-discount__pill-remove")) {
      this.querySelector(".cart-discount__pill-remove").addEventListener('click', this.removeDiscount.bind(this))
    }
  }

  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      this.cartDiscountError.classList.add('hidden');
      this.cartDiscountErrorDiscountCode.classList.add('hidden');
      this.cartDiscountErrorShipping.classList.add('hidden');


      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(`${routes.cart_update_url}`, {
        ...config,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }

        const oldCartItems = this.closest('.cart-drawer')?.querySelector('.cart-items')
        const newCartItems = section.querySelector('.cart-items')
        if (oldCartItems && newCartItems) {
          oldCartItems.innerHTML = newCartItems.innerHTML;
        }

        const oldDiscountDetails = this.closest('.drawer__footer')?.querySelector('.cart-discount__codes');
        const newDiscountDetails = section.querySelector('.drawer__footer .cart-discount__codes')
        if (oldDiscountDetails && newDiscountDetails) {
          oldDiscountDetails.innerHTML = newDiscountDetails.innerHTML;
        }

        const oldCartDrawerFooter = this.closest('.drawer__footer')?.querySelector('.cart-drawer__footer')
        const newCartDrawerFooter = section.querySelector('.drawer__footer .cart-drawer__footer')
        if (oldCartDrawerFooter && newCartDrawerFooter) {
          oldCartDrawerFooter.innerHTML = newCartDrawerFooter.innerHTML;
        }

        if (this.querySelectorAll(".cart-discount__pill-remove").length > 0) {
          this.querySelectorAll(".cart-discount__pill-remove").forEach((removeButton) => {
            const removeButtonPill = removeButton.closest('.cart-discount__pill')
            if (removeButtonPill && !(existingDiscounts.includes(removeButtonPill.dataset.discountCode))) {
              removeButton.addEventListener('click', this.removeDiscount.bind(this))
            }
          })
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(`${routes.cart_update_url}`, {
        ...config,
      });

      const data = await response.json();

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);

      if (section) {
        const oldCartItems = this.closest('.cart-drawer')?.querySelector('.cart-items')
        const newCartItems = section.querySelector('.cart-items')
        if (oldCartItems && newCartItems) {
          oldCartItems.innerHTML = newCartItems.innerHTML;
        }
        
        const oldDiscountDetails = this.closest('.drawer__footer')?.querySelector('.cart-discount__codes');
        const newDiscountDetails = section.querySelector('.drawer__footer .cart-discount__codes')
        if (oldDiscountDetails && newDiscountDetails) {
          oldDiscountDetails.innerHTML = newDiscountDetails.innerHTML;
        }

        const oldCartDrawerFooter = this.closest('.drawer__footer')?.querySelector('.cart-drawer__footer')
        const newCartDrawerFooter = section.querySelector('.drawer__footer .cart-drawer__footer')
        if (oldCartDrawerFooter && newCartDrawerFooter) {
          oldCartDrawerFooter.innerHTML = newCartDrawerFooter.innerHTML;
        }

        if (this.querySelectorAll(".cart-discount__pill-remove").length > 0) {
          this.querySelectorAll(".cart-discount__pill-remove").forEach((removeButton) => {
            removeButton.addEventListener('click', this.removeDiscount.bind(this))
          })
        }
      }
    } catch (error) {
      console.error(error)
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const target = type === 'discount_code' ? this.cartDiscountErrorDiscountCode : this.cartDiscountErrorShipping;
    this.cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
